/**
 * Composant Link - Lien de navigation avec gestion des événements
 * 
 * @component
 * @param {Object} props - Props du composant
 * @param {string} props.to - URL de destination
 * @param {React.ReactNode} props.children - Contenu du lien
 * @param {Function} props.onClick - Callback de clic
 * @param {string} props.className - Classe CSS additionnelle
 * @param {boolean} props.external - Lien externe (défaut: false)
 * @param {string} props.target - Target du lien (pour liens externes)
 * @param {string} props.rel - Relation du lien (pour liens externes)
 * @returns {JSX.Element} Composant Link rendu
 */

import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import history from '../../history';

/**
 * Vérifie si l'événement est un clic gauche
 * @param {MouseEvent} event - Événement de clic
 * @returns {boolean} True si c'est un clic gauche
 */
function isLeftClickEvent(event) {
  return event.button === 0;
}

/**
 * Vérifie si l'événement est modifié (Ctrl, Alt, etc.)
 * @param {MouseEvent} event - Événement de clic
 * @returns {boolean} True si l'événement est modifié
 */
function isModifiedEvent(event) {
  return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}

/**
 * Gère le clic sur le lien
 * @param {Object} props - Props du composant
 * @param {MouseEvent} event - Événement de clic
 */
function handleClick(props, event) {
  event.stopPropagation();

  // Appeler le callback onClick si fourni
  if (props.onClick) {
    props.onClick(event);
  }

  // Ne pas intercepter les clics modifiés ou non-gauche
  if (isModifiedEvent(event) || !isLeftClickEvent(event)) {
    return;
  }

  // Ne pas intercepter si l'événement est déjà prévenu
  if (event.defaultPrevented === true) {
    return;
  }

  // Empêcher le comportement par défaut et naviguer
  event.preventDefault();
  history.push(props.to);
}

/**
 * Composant Link - Lien de navigation avec gestion des événements
 */
const Link = ({ 
  to, 
  children, 
  onClick, 
  className = 'link', 
  external = false,
  target,
  rel,
  ...attrs 
}) => {
  // Handler optimisé avec useCallback
  const handleClickCallback = useCallback(
    (event) => handleClick({ to, onClick }, event),
    [to, onClick]
  );

  // Props pour les liens externes
  const externalProps = external ? {
    target: target || '_blank',
    rel: rel || 'noopener noreferrer'
  } : {};

  return (
    <a 
      className={className} 
      href={to} 
      onClick={handleClickCallback}
      {...externalProps}
      {...attrs}
    >
      {children}
    </a>
  );
};

// PropTypes complets
Link.propTypes = {
  to: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  className: PropTypes.string,
  external: PropTypes.bool,
  target: PropTypes.string,
  rel: PropTypes.string,
};

// Les paramètres par défaut sont définis dans la destructuration des props

export default Link;